import { CSSProperties, ReactElement } from 'react';
import './TableSkeleton.css';

export interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  className?: string;
  showHeader?: boolean;
  rowHeight?: number;
}

/**
 * Reusable table skeleton placeholder for PIM tables. Renders a grid of muted
 * rounded bars while data loads, replacing a single spinner over stale rows.
 * With `showHeader`, a header bar is drawn so the skeleton mirrors the grid it
 * becomes; `rowHeight` matches the real row height so nothing reflows on swap.
 */
export function TableSkeleton({
  rows = 8,
  columns = 6,
  className,
  showHeader = false,
  rowHeight,
}: TableSkeletonProps): ReactElement {
  const rowKeys = Array.from({ length: rows }, (_, index) => `pim-skeleton-row-${index}`);
  const columnKeys = Array.from({ length: columns }, (_, index) => `pim-skeleton-col-${index}`);
  const rootClass = ['pim-table-skeleton', className].filter(Boolean).join(' ');
  const rowStyle: CSSProperties | undefined = rowHeight != null ? { height: rowHeight } : undefined;
  return (
    <div className={rootClass} aria-hidden="true">
      {showHeader && (
        <div className="pim-table-skeleton__header" style={rowStyle}>
          {columnKeys.map((columnKey) => (
            <span key={columnKey} className="pim-table-skeleton__cell pim-table-skeleton__cell--header" />
          ))}
        </div>
      )}
      {rowKeys.map((rowKey) => (
        <div key={rowKey} className="pim-table-skeleton__row" style={rowStyle}>
          {columnKeys.map((columnKey) => (
            <span key={columnKey} className="pim-table-skeleton__cell" />
          ))}
        </div>
      ))}
    </div>
  );
}

export default TableSkeleton;
